import { Suspense, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { lazyRetry } from '../lazyRetry';
import { Icon } from './Icon';

/** 시트 본체(초안 · 예상 건수 · 포커스 가둠)는 열 때만 내려받는다 — 주 번들 300KB 안 (루브릭 H3) */
const FilterSheetDialog = lazyRetry(() => import('./FilterSheetDialog')) as unknown as <T>(p: SheetProps<T> & { id: string; onClose: () => void }) => JSX.Element;

/** 화면 폭 조건 — 휴대폰(< 768) 필터 시트 · 시트 안 배치에 쓴다 */
export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    // change 만으로는 기기 회전 · 창 크기 바꾸기를 놓치는 브라우저(에뮬레이션 포함)가 있어 resize 도 함께 듣는다
    (fn) => {
      const m = window.matchMedia(query);
      m.addEventListener('change', fn); window.addEventListener('resize', fn);
      return () => { m.removeEventListener('change', fn); window.removeEventListener('resize', fn); };
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export interface AppliedChip { key: string; label: string; clear: () => void }

export interface SheetProps<T> {
  /** 지금 적용된 조건 — 시트를 열 때 초안으로 복사한다 */
  value: T;
  /** 「초기화」 가 만드는 빈 조건 */
  empty: T;
  onApply: (v: T) => void;
  /** 적용 전 예상 건수 — 초안이 바뀔 때마다(300ms 뒤) 묻는다. 이전 요청은 취소 */
  preview: (v: T, signal: AbortSignal) => Promise<number>;
  chips: AppliedChip[];
  children: (draft: T, setDraft: (next: T) => void) => ReactNode;
}

/**
 * 휴대폰(< 768) 필터 아래 시트 (계획서 U14 · 루브릭 R3-9) — 이 파일은 여는 버튼과 적용된 칩만.
 * 버튼 「필터 (n)」 → 화면 아래에서 올라오는 대화상자(FilterSheetDialog): 고른 것은 초안에 모았다가 「적용 · n건」 으로 한 번에.
 * 닫히면 「필터」 버튼으로 포커스가 돌아온다.
 */
export default function FilterSheet<T>(props: SheetProps<T>) {
  const { chips } = props;
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const n = chips.length;
  const close = () => { setOpen(false); window.setTimeout(() => btn.current?.focus(), 0); };
  // Codex 검토: 시트를 연 채 폭을 768 이상으로 넓히면 이 줄이 display:none 이 되는데 시트는 열린 채 남아
  // 스크롤 잠금 · 키 가둠이 풀리지 않았다 — 레일이 다시 보이는 폭이 되면 초안을 버리고 닫는다(포커스는 레일 쪽에 둔다)
  const wide = useMedia('(min-width: 768px)');
  useEffect(() => { if (open && wide) setOpen(false); }, [open, wide]);

  return (
    <div className="sheet-bar">
      <button type="button" ref={btn} className="btn sm sheet-open" aria-haspopup="dialog" aria-expanded={open}
              aria-controls={open ? id : undefined} onClick={() => setOpen(true)}>
        <Icon name="sliders" size={16} /> 필터{n ? ` (${n})` : ''}
      </button>
      {n > 0 && (
        <div className="facet-chips sheet-chips" aria-label="적용된 필터">
          {chips.map((c) => (
            <button type="button" className="chip-x" key={c.key} onClick={c.clear} aria-label={`${c.label} 필터 해제`}>
              {c.label} <Icon name="close" size={12} />
            </button>
          ))}
        </div>
      )}
      {open && (
        <Suspense fallback={<div className="sheet-overlay" role="status"><span className="sr-only">필터를 여는 중…</span></div>}>
          <FilterSheetDialog<T> {...props} id={id} onClose={close} />
        </Suspense>
      )}
    </div>
  );
}
